import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useGrantWhiteboardPermission } from "@src/components/board/mutations/grant-whiteboard-permission";
import { whiteboardQueryKeys } from "@src/components/board/queries/query-keys";
import { parseWhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { useGrantCardPermission } from "@src/components/library/queries/card-operations";
import { libraryQueryKeys } from "@src/components/library/queries/query-keys";
import { CardPermission, WhiteboardPermission } from "@src/gql/graphql";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MentionUserAvatar } from "../ui/mention-user-avatar";
import {
	type OpenInviteCollaboratorOptions,
	openInviteCollaboratorModal,
	useInviteCollaboratorStore,
} from "./invite-collaborator-store";

export {
	type OpenInviteCollaboratorOptions,
	openInviteCollaboratorModal,
	useInviteCollaboratorStore,
};

export function InviteCollaboratorPopover() {
	const isOpen = useInviteCollaboratorStore((state) => state.isOpen);
	const options = useInviteCollaboratorStore((state) => state.options);

	if (!isOpen || !options) return null;

	return <InviteCollaboratorPopoverContent options={options} />;
}

function InviteCollaboratorPopoverContent({
	options,
}: {
	options: OpenInviteCollaboratorOptions;
}) {
	const close = useInviteCollaboratorStore((state) => state.close);
	const [loadingRole, setLoadingRole] = useState<"editor" | "viewer" | null>(
		null,
	);
	const queryClient = useQueryClient();
	const { mutateAsync: grantWhiteboardPermission } =
		useGrantWhiteboardPermission();
	const { mutateAsync: grantCardPermission } = useGrantCardPermission();

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				if (loadingRole) return;
				options.onConfirmed?.(false);
				close();
			}
		};

		window.addEventListener("keydown", handleKeyDown, true);
		return () => window.removeEventListener("keydown", handleKeyDown, true);
	}, [options, close, loadingRole]);

	const targetTitle =
		options.targetTitle ?? (options.whiteboardId ? "board" : "library card");
	const { person, onConfirmed, anchorRect } = options;

	const handleInvite = async (selectedRole: "editor" | "viewer") => {
		setLoadingRole(selectedRole);
		try {
			if (options.onInvite) {
				await options.onInvite(selectedRole);
			} else if (options.whiteboardId) {
				if (!person.email) {
					throw new Error(
						"Cannot invite collaborator without an email address",
					);
				}
				const whiteboardIdStr = parseWhiteboardDocumentId(
					options.whiteboardId,
				).toIdString();
				const permission =
					selectedRole === "editor"
						? WhiteboardPermission.Write
						: WhiteboardPermission.Read;

				await grantWhiteboardPermission({
					whiteboardId: whiteboardIdStr,
					userEmails: [person.email.trim().toLowerCase()],
					permission,
				});

				await queryClient.invalidateQueries({
					queryKey: whiteboardQueryKeys.ofId(whiteboardIdStr),
				});
				await queryClient.invalidateQueries({
					queryKey: whiteboardQueryKeys.all,
				});
			} else if (options.cardId) {
				if (!person.email) {
					throw new Error(
						"Cannot invite collaborator without an email address",
					);
				}
				const cardPermission =
					selectedRole === "editor"
						? CardPermission.Write
						: CardPermission.Read;

				await grantCardPermission({
					cardId: options.cardId,
					email: person.email.trim().toLowerCase(),
					role: cardPermission,
					initialize: true,
				});

				await queryClient.invalidateQueries({
					queryKey: libraryQueryKeys.myCard.all,
				});
				await queryClient.invalidateQueries({
					queryKey: libraryQueryKeys.myCard.byId(options.cardId),
				});
			}

			notifications.show({
				title: "Access granted",
				message: `Shared ${targetTitle} with ${person.displayName} as ${selectedRole}.`,
				color: "green",
			});

			onConfirmed?.(true);
			close();
		} catch (error) {
			notifications.show({
				title: "Failed to grant access",
				message:
					error instanceof Error
						? error.message
						: "An error occurred while granting permissions.",
				color: "red",
			});
		} finally {
			setLoadingRole(null);
		}
	};

	const handleProceedWithoutInvite = () => {
		if (loadingRole) return;
		onConfirmed?.(false);
		close();
	};

	// Calculate popover coordinates near caret, clamped to viewport
	const popoverWidth = 320;
	const popoverHeight = 280;
	const targetX = anchorRect?.x ?? window.innerWidth / 2 - popoverWidth / 2;
	const targetY = anchorRect?.y ?? window.innerHeight / 2 - popoverHeight / 2;

	const x = Math.min(
		Math.max(16, targetX),
		window.innerWidth - popoverWidth - 16,
	);
	const y = Math.min(
		Math.max(16, targetY),
		window.innerHeight - popoverHeight - 16,
	);

	const content = (
		<>
			<div
				aria-hidden="true"
				className="fixed inset-0 z-[9998]"
				data-invite-collaborator-backdrop
				onClick={handleProceedWithoutInvite}
			/>

			<div
				aria-label={`Invite to ${targetTitle}`}
				className={clsx(
					"fixed z-[9999] w-80 overflow-hidden rounded-xl",
					"border border-border-default bg-surface-default p-4",
					"shadow-2xl animate-in fade-in zoom-in-95 duration-100",
				)}
				data-invite-collaborator-popover
				style={{
					left: `${x}px`,
					top: `${y}px`,
				}}
			>
				<div className="mb-2.5 flex items-center justify-between">
					<span
						className={clsx(
							"text-xs font-semibold uppercase tracking-wider",
							"text-text-secondary",
						)}
					>
						Invite to {targetTitle}
					</span>
					<button
						aria-label="Close"
						className={clsx(
							"flex size-6 items-center justify-center rounded-md",
							"text-text-tertiary transition-colors",
							"hover:bg-surface-hover-subtle hover:text-text-primary",
							loadingRole && "pointer-events-none opacity-50",
						)}
						disabled={loadingRole !== null}
						onClick={handleProceedWithoutInvite}
						type="button"
					>
						✕
					</button>
				</div>

				<div className="flex min-w-0 items-center gap-2.5">
					<MentionUserAvatar
						avatarUrl={person.avatarUrl}
						name={person.displayName}
						size="md"
					/>
					<div className="min-w-0 flex-1">
						<div className="truncate text-sm font-semibold text-text-primary">
							{person.displayName}
						</div>
						{person.email && (
							<div className="truncate text-xs text-text-tertiary">
								{person.email}
							</div>
						)}
					</div>
				</div>

				<div
					className={clsx(
						"mt-3 rounded-lg bg-surface-subtle p-2",
						"text-xs text-text-secondary",
					)}
				>
					<span className="font-medium text-text-primary">
						{person.displayName}
					</span>{" "}
					does not have access to this {targetTitle}.
				</div>

				<div className="mt-4 flex flex-col gap-2">
					<Button
						disabled={loadingRole !== null}
						loading={loadingRole === "editor"}
						onClick={() => handleInvite("editor")}
						size="xs"
						variant="filled"
					>
						Invite as editor & mention
					</Button>
					<Button
						disabled={loadingRole !== null}
						loading={loadingRole === "viewer"}
						onClick={() => handleInvite("viewer")}
						size="xs"
						variant="default"
					>
						Invite as viewer & mention
					</Button>
					<Button
						disabled={loadingRole !== null}
						onClick={handleProceedWithoutInvite}
						size="xs"
						variant="subtle"
					>
						Mention only
					</Button>
				</div>
			</div>
		</>
	);

	return typeof document !== "undefined"
		? createPortal(content, document.body)
		: null;
}
